/**
 * Odoo Authentication Service
 *
 * Handles authentication with Odoo backend using Azure Entra ID tokens.
 */

// In development, use relative URLs to go through Vite proxy
// In production, use the configured ODOO_BASE_URL
const ODOO_BASE_URL =
  import.meta.env.MODE === 'development'
    ? '' // Use relative URLs in development (proxy will forward to localhost:8069)
    : import.meta.env.VITE_ODOO_BASE_URL || 'http://localhost:8069';

export interface OdooAuthResponse {
  email?: string;
  error?: string;
  error_description?: string;
  is_authenticated?: boolean;
  login?: string;
  name?: string;
  department?: string;
  role?: 'admin' | 'user';
  session_id?: string;
  success?: boolean;
  uid?: number;
}

interface OdooError {
  data?: {
    message?: string;
    name?: string;
  };
  message?: string;
}

interface OdooJsonRpcResponse {
  error?: OdooError;
  result?: OdooAuthResponse;
}

/**
 * Authenticate with Odoo using Azure Entra ID tokens
 */
export async function authenticateWithOdoo(
  idToken: string,
  accessToken?: string
): Promise<OdooAuthResponse> {
  try {
    const response = await fetch(`${ODOO_BASE_URL}/api/auth/entra`, {
      body: JSON.stringify({
        id: Date.now(),
        jsonrpc: '2.0',
        method: 'call',
        params: {
          access_token: accessToken,
          id_token: idToken
        }
      }),
      credentials: 'include',
      headers: {
        'Content-Type': 'application/json'
      },
      method: 'POST'
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${String(response.status)}`);
    }

    const data = (await response.json()) as OdooJsonRpcResponse;

    if (data.error) {
      console.error('Odoo authentication error');
      return {
        error: data.error.data?.name ?? 'authentication_failed',
        error_description:
          data.error.data?.message ?? data.error.message ?? 'Unknown error'
      };
    }

    return (
      data.result ?? {
        error: 'unknown_error',
        error_description: 'No result returned from Odoo'
      }
    );
  } catch (error) {
    console.error('Error authenticating with Odoo');
    return {
      error: 'network_error',
      error_description:
        error instanceof Error ? error.message : 'Unknown error'
    };
  }
}

/**
 * Check if there's a valid Odoo session
 */
export async function checkOdooSession(): Promise<OdooAuthResponse> {
  try {
    const response = await fetch(`${ODOO_BASE_URL}/api/auth/session`, {
      body: JSON.stringify({
        id: Date.now(),
        jsonrpc: '2.0',
        method: 'call',
        params: {}
      }),
      credentials: 'include',
      headers: {
        'Content-Type': 'application/json'
      },
      method: 'POST'
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${String(response.status)}`);
    }

    const data = (await response.json()) as OdooJsonRpcResponse;

    if (data.error) {
      return {
        error: data.error.data?.name ?? 'session_check_failed',
        error_description:
          data.error.data?.message ?? data.error.message ?? 'Unknown error'
      };
    }

    return (
      data.result ?? {
        error: 'unknown_error',
        error_description: 'No result returned from Odoo'
      }
    );
  } catch (error) {
    console.error('Error checking Odoo session');
    return {
      error: 'network_error',
      error_description:
        error instanceof Error ? error.message : 'Unknown error'
    };
  }
}

/**
 * Logout from Odoo
 */
export async function logoutFromOdoo(): Promise<{
  error?: string;
  success: boolean;
}> {
  try {
    const response = await fetch(`${ODOO_BASE_URL}/api/auth/logout`, {
      body: JSON.stringify({
        id: Date.now(),
        jsonrpc: '2.0',
        method: 'call',
        params: {}
      }),
      credentials: 'include',
      headers: {
        'Content-Type': 'application/json'
      },
      method: 'POST'
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${String(response.status)}`);
    }

    const data = (await response.json()) as OdooJsonRpcResponse;

    if (data.error) {
      return {
        error:
          data.error.data?.message ?? data.error.message ?? 'Unknown error',
        success: false
      };
    }

    if (!data.result) {
      return {
        error: 'unknown_error',
        success: false
      };
    }

    return {
      error: data.result.error,
      success: data.result.success ?? false
    };
  } catch (error) {
    console.error('Error logging out from Odoo');
    return {
      error: error instanceof Error ? error.message : 'Unknown error',
      success: false
    };
  }
}
